import * as clipboard from 'clipboard-polyfill'
import {Lang, langEn, langRu} from 'yougile-lang'
import {logger, Util} from 'yougile-util'
import {
  DataObjectId,
  duplicateRole,
  EntitledHubLike,
  Hub,
  HubLike,
  isDataObjectId,
  noBoardContainerId,
  PermissionCustomRole,
  ProjectSettings,
  ProjectType,
  relinkRolePermissionsChildren,
  SandboxTree,
  User
} from 'yougile-model'
import {Items} from '@api/model/items'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {SyncEvents} from '@client/sync-events'
import {Backend} from '@common/backend'
import {MessageBox} from '@components/message-box'
import {Gantt} from '@extensions/gantt'
import {GanttDraft} from '@extensions/gantt-draft'
import {Board} from './board'
import {globals} from './globals-base'
import {Order} from './order'
import {Sticker} from './sticker/sticker'
import {SystemEntities} from './system-entities'
import {UserAccess} from './user-access'

/**
 * Project structure:
 * User * -> * Company Account -> * Project -> * Board -> * List -> * Task
 */
export class Project {
  /**
   * projectId -> order
   */
  private static boardOrders: Map<DataObjectId, Order> = new Map()
  private static cachedProjectsOrder: Map<DataObjectId, DataObjectId[]> =
    new Map()
  private static initialized = false

  public static init(): void {
    if (Project.initialized) {
      return
    }
    Project.initialized = true
    SyncEvents.addAnyListener(companyId => {
      Project.cachedProjectsOrder.delete(companyId)
    })
  }

  public static clean(): void {
    for (const order of Project.boardOrders.values()) {
      order.cleanup()
    }
    Project.boardOrders.clear()
  }

  public static getOrder(companyId: DataObjectId): DataObjectId[] | undefined {
    const box = Sync.get({companyId})
    if (!box) {
      return undefined
    }
    const cached = Project.cachedProjectsOrder.get(companyId)
    if (cached) {
      return cached
    }
    const company = box.get(box.companyId) as Hub
    const user = box.get(globals.currentUserId) as User
    if (!box.isConnected(user.id, company.id)) {
      box.connect(user.id, company.id)
    }
    const isAdmin = PermissionsChecker.isAdmin()
    const ids = (box.getModifiers(user.id, company.id) as DataObjectId[]) || []
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
    const res = ids
      .map(id => box.get(id) as Hub)
      .filter(
        p =>
          !!p &&
          (isAdmin ||
            PermissionsChecker.checkShow('ignore-departments', p, {
              calculatedHierarchy
            }))
      )
      .map(p => p.id)
    Project.cachedProjectsOrder.set(companyId, res)
    return res
  }

  public static fixOrderIfNeeded(companyId: DataObjectId): void {
    const order = Project.getOrder(companyId)
    if (!order) {
      return
    }
    const box = Sync.get({companyId})
    if (!box) {
      return
    }
    let added = false
    const allProjects = Project.listProjects(box.companyId, 'all')
    allProjects.forEach(p => {
      if (!p) {
        return
      }
      if (!order.includes(p.id)) {
        order.push(p.id)
        added = true
        logger.log('FIX ORDER: Added project to order', p.title)
      }
    })
    if (added) {
      Project.setOrder(companyId, order)
    }
    const filtered = order.filter(id => box.get(id) && !box.get(id).deleted)
    if (filtered.length < order.length) {
      logger.log(
        'FIX ORDER: Removed old projects from order',
        order.length,
        filtered.length
      )
      Project.setOrder(companyId, filtered)
    }
  }

  static getLink(project: Hub): string {
    const host = location.host
      ? location.protocol + '//' + location.host
      : Backend.toMainUrl('')

    const shortCompanyId = Util.shortId(project.companyId)

    return `${host}/team/${shortCompanyId}/${encodeURIComponent(project.title)}`
  }

  static copyLink(project: Hub): void {
    const url = Project.getLink(project)

    clipboard
      .writeText(url)
      .then(() =>
        MessageBox.show(Lang.msg.permission.regProjectLinkCopied, 'success', {
          timer: 2000
        })
      )
      .catch(e => logger.log('err', e))
  }

  public static setOrder(
    companyId: DataObjectId,
    projectsOrder: DataObjectId[]
  ): void {
    const box = Sync.get({companyId})
    const company = box.get(box.companyId) as Hub
    const user = box.get(globals.currentUserId) as User
    if (!box.isConnected(user.id, company.id)) {
      box.connect(user.id, company.id)
    }
    box.setModifiers(user.id, company.id, projectsOrder)
  }

  /**
   * It creates new project inside user's default company account.
   * And also initializes it with Main board.
   */
  public static createNew(
    companyId: DataObjectId,
    title: string,
    withDefaults = true,
    allowApi = false,
    withId?: DataObjectId,
    type?: ProjectType
  ): Hub {
    title = title.trim()

    if (allowApi && globals.getCompanyId() === companyId) {
      if (!Items.beforeAdd(Items.get(companyId), title)) {
        return undefined
      }
    }
    const newPr = new Hub(companyId, title)
    if (withId) {
      newPr.id = withId
    }

    const box = Sync.get({companyId})
    newPr.data.by = globals.currentUserId

    // to allow server accept later changes
    newPr.data.userPermissions = {[globals.currentUserId]: 'admin'}

    box.add(newPr, companyId, true, false)
    const boards = []

    if (type === 'crm') {
      newPr.data.projectType = type
      const crmTemplateBoard = this.createCrmTemplateBoard(box, newPr)
      boards.push(crmTemplateBoard)
    } else {
      if (withDefaults) {
        const board = Project.addBoard(Lang.msg.newBoard, newPr, true, allowApi)
        boards.push(board)
      }
    }

    Order.init(newPr, newPr, boards)
    UserAccess.setRoleInProject(globals.currentUserId, newPr, 'admin')

    const createdBy = SystemEntities.get(companyId).createdByTagId
    if (globals.currentUserId) {
      box.connect(createdBy, newPr.id)
      box.addModifiers(createdBy, newPr.id, [globals.currentUserId])
    }

    if (allowApi && globals.getCompanyId() === companyId) {
      Items.add(Items.fromHub(newPr))
    }

    const order = Project.getOrder(companyId) ?? []
    order.splice(0, 0, newPr.id)
    Project.setOrder(box.companyId, order)
    return newPr
  }

  public static duplicate(project: Hub): void {
    const localOldNewMap = new Map<DataObjectId, DataObjectId>()
    const box = Sync.get(project)
    const title = box.notConflictingTitle(project.title, 'Hub', box.companyId)
    const dup = Project.createNew(box.companyId, title, false, true)
    if (!dup) {
      return
    }
    if (project.properties) {
      dup.properties = Util.clone(project.properties)
    }
    dup.description = project.description
    if (project.stickers) {
      dup.stickers = Util.clone(project.stickers)
    }
    const ord = Project.getOrder(box.companyId)
    if (ord) {
      const ix = ord.indexOf(project.id) + 1
      ord.splice(ix, 0, dup.id)
      Project.setOrder(box.companyId, ord)
    }
    dup.commit()

    const boards = Project.listBoards(project)
    Project.sortBoards(project, boards)
    boards.forEach(b => Board.duplicate(b, dup, localOldNewMap))

    if (project.data.roles) {
      Project.duplicateRoles(project.data.roles, dup, localOldNewMap)
    }

    if (project.data.userPermissions) {
      dup.data.userPermissions = Util.clone(project.data.userPermissions)
      for (const [key, value] of Object.entries(dup.data.userPermissions)) {
        if (isDataObjectId(value)) {
          const keyInMap = localOldNewMap.get(value)
          if (keyInMap) {
            dup.data.userPermissions[key as DataObjectId] = keyInMap
          }
        }
      }
    }

    dup.commit()

    SyncCommitHandler.doInBatch(() => {
      for (const newId of localOldNewMap.values()) {
        Gantt.relinkTaskDependencies(newId, localOldNewMap)
      }
    })
  }

  public static sortBoards(
    project: HubLike,
    boards: Hub[],
    reverse = true
  ): void {
    if (!project || project.id === noBoardContainerId) {
      return
    }
    const boardsOrder = Project.getBoardsOrder(project)
    boards.sort(
      (a, b) =>
        (reverse ? 1 : -1) * (boardsOrder.order(b) - boardsOrder.order(a))
    )
  }

  public static listBoards(project: HubLike, sorted = false): Hub[] {
    if (!project || project.id === noBoardContainerId) {
      return []
    }
    const res = Sync.get(project)
      .getChildrenHubs(project.id)
      .filter(h => PermissionsChecker.checkShow('ignore-departments', h, {}))
    if (sorted) {
      Project.sortBoards(project, res, false)
    }
    return res
  }

  public static projectOrder(
    companyId: DataObjectId,
    id: DataObjectId
  ): number {
    const ix = Project.getOrder(companyId).indexOf(id)
    return ix > -1 ? ix : 1000
  }

  public static projectOrderComparer(first: Hub, second: Hub): number {
    const {projectOrder: order} = Project

    return order(first.companyId, first.id) - order(second.companyId, second.id)
  }

  public static getBoardsOrder(project: HubLike): Order {
    if (project.id === noBoardContainerId) {
      logger.log('noBoardsContaner was treated like project')
      return new Order(project.companyId)
    }
    if (!Project.boardOrders.has(project.id)) {
      const order = new Order(project.companyId)
      order.load(
        project.id,
        project.id,
        Sync.get(project).getChildrenHubs(project.id)
      )
      Project.boardOrders.set(project.id, order)
    }
    return Project.boardOrders.get(project.id)
  }

  public static addBoard(
    title: string,
    project: Hub,
    withDefaults = true,
    allowApi = false,
    toEnd = true
  ): Hub {
    title = title.trim()
    const box = Sync.get(project)

    if (allowApi && globals.getCompanyId() === project.companyId) {
      if (!Items.beforeAdd(Items.fromHub(project), title)) {
        return undefined
      }
    }
    const newBoard = new Hub(project.companyId, title)

    Sync.get(project).add(newBoard, project.id, true, false)
    const col = withDefaults
      ? Board.addColumn(Lang.msg.newColumn, newBoard, allowApi)
      : undefined
    const columns = col ? [col] : []
    Order.init(newBoard, newBoard, columns)
    if (withDefaults) {
      const {addToBoard, getByName} = Sticker
      addToBoard(newBoard, getByName(box, 'User'))
      addToBoard(newBoard, getByName(box, 'Deadline'))
      const priority =
        getByName(box, langRu.stickerPriority) ||
        getByName(box, langEn.stickerPriority)
      if (priority) {
        addToBoard(newBoard, priority)
      }
    }
    Project.getBoardsOrder(project).addEntity(newBoard, toEnd)

    if (allowApi && globals.getCompanyId() === project.companyId) {
      Items.add(Items.fromHub(newBoard))
    }

    const companyPreferences = CompanyPreferencesClient.get(project.companyId)
    if (companyPreferences.setAutoTaskCoverForNewBoards) {
      newBoard.data.settings = {
        ...newBoard.data.settings,
        autoTaskCover: {checked: true, values: []}
      }
    }

    return newBoard
  }

  public static createCrmTemplateBoard(box: SandboxTree, project: Hub): Hub {
    const board = Project.addBoard(
      Lang.msg.crm.board.title,
      project,
      false,
      true
    )

    const contactPersonsBoard = Project.addBoard(
      // NOTE: starts with dot, to hide it from the UI
      '.crmContactPersonsBoard',
      project,
      false,
      false
    )
    const contactPersonsColumnHub = Board.addColumn(
      // NOTE: starts with dot, to hide it from the UI
      '.crmContactPersonsLocation',
      contactPersonsBoard,
      false
    )

    const organizationsBoard = Project.addBoard(
      // NOTE: starts with dot, to hide it from the UI
      '.crmOrganizationsBoard',
      project,
      false,
      false
    )
    const organizationsColumnHub = Board.addColumn(
      // NOTE: starts with dot, to hide it from the UI
      '.crmOrganizationsLocation',
      organizationsBoard,
      false
    )

    project.data.projectSettings ??= {} as ProjectSettings
    project.data.projectSettings.contactPersonsLocation =
      contactPersonsColumnHub.id
    project.data.projectSettings.organizationsLocation =
      organizationsColumnHub.id

    contactPersonsBoard.commit()
    organizationsBoard.commit()

    Board.createCrmTemplateColumns(box, board)

    return board
  }

  public static removeBoard(board: HubLike): void {
    const box = Sync.get(board)
    const hub = box.get(board.id) as Hub
    if (hub) {
      box.remove(hub)
      Project.getBoardsOrder(box.get(hub.location) as Hub).cleanDeleted()
      GanttDraft.planEntitySubtree(hub.id, box)
    }
  }

  public static listProjects(
    companyId: DataObjectId,
    archiveType: 'not-archive' | 'archive' | 'all',
    showOnlyUserProjects?: boolean
  ): Hub[] {
    if (SharedBoardsClient.getId()) {
      return []
    }
    const res: Hub[] = []
    const box = Sync.get({companyId})
    const userId = globals.currentUserId
    box.walkForward(box.getHub(box.companyId), (obj, level) => {
      if (level > 0 && (obj.deleted || obj.title.startsWith('.'))) {
        return false
      }
      if (level === 1) {
        const show =
          PermissionsChecker.checkShow('ignore-departments', obj, {
            ignoreAdmin: showOnlyUserProjects
          }) || !!UserAccess.getRoleInProject(userId, obj)
        if (show) {
          if (
            archiveType === 'all' ||
            (archiveType === 'archive' && obj.complete) ||
            (archiveType === 'not-archive' && !obj.complete)
          ) {
            res.push(obj)
          }
        }
        return false
      }
      return true
    })
    return res
  }

  public static find({id, companyId}: HubLike): Hub {
    const box = Sync.get({companyId})
    return box && (box.get(id) as Hub)
  }

  public static editTitle(project: EntitledHubLike, title: string): void {
    if (project.title !== title) {
      const hub = Project.find(project)
      const canEdit = Items.rename(Items.fromHub(hub), title)

      if (canEdit) {
        Sync.get(project).editTitle(hub, title)
      }
    }
  }

  public static remove(project: HubLike): void {
    const box = Sync.get(project)
    const hub = Project.find(project)
    box.remove(hub)
    GanttDraft.planEntitySubtree(hub.id, box)
  }

  private static duplicateRoles = (
    roles: {[key: string]: PermissionCustomRole},
    toProject: Hub,
    oldNewMap: Map<DataObjectId, DataObjectId>
  ): void => {
    const dupRoles: typeof roles = {}

    for (const role of Object.values(roles)) {
      const dup = duplicateRole(role)
      if (isDataObjectId(role.key) && isDataObjectId(dup.key)) {
        oldNewMap.set(role.key, dup.key)
      }
      dupRoles[dup.key] = dup

      if (dup.permissions.children) {
        dup.permissions.children = relinkRolePermissionsChildren(
          role.permissions.children,
          oldNewMap
        )
      }
    }

    toProject.data.roles = dupRoles
    toProject.commit()
  }
}
